import { ref, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { productsAPI } from '@/api/modules/products.js'
import { useCategoriesStore } from '@/stores/categories'

export function useSearchDropdown() {
  const router = useRouter()
  const categoriesStore = useCategoriesStore()
  
  // State
  const searchQuery = ref('')
  const isDropdownOpen = ref(false)
  const isLoading = ref(false)
  const searchResults = ref({
    products: [],
    categories: [],
    sets: []
  })
  const searchTimeout = ref(null)
  const selectedIndex = ref(-1)
  
  // Computed
  const hasResults = computed(() => 
    searchResults.value.products.length > 0 ||
    searchResults.value.categories.length > 0 ||
    searchResults.value.sets.length > 0
  )
  
  const totalResults = computed(() => 
    searchResults.value.products.length +
    searchResults.value.categories.length +
    searchResults.value.sets.length
  )
  
  const allItems = computed(() => {
    const items = []
    
    // Add categories first
    searchResults.value.categories.forEach((category, index) => {
      items.push({ type: 'category', data: category, index: index })
    })
    
    // Add sets
    searchResults.value.sets.forEach((set, index) => {
      items.push({
        type: 'set',
        data: set,
        index: searchResults.value.categories.length + index
      })
    })
    
    // Add products
    searchResults.value.products.forEach((product, index) => {
      items.push({
        type: 'product',
        data: product,
        index: searchResults.value.categories.length + searchResults.value.sets.length + index
      })
    })
    
    return items
  })
  
  // Methods
  const performSearch = async (query) => {
    if (!query || query.length < 2) {
      searchResults.value = { products: [], categories: [], sets: [] }
      isDropdownOpen.value = false
      return
    }
    
    try {
      isLoading.value = true
      
      // Search products
      const productsResponse = await productsAPI.searchProducts({
        q: query,
        per_page: 5
      })
      
      // Search categories (filter from store)
      const filteredCategories = categoriesStore.categories
        .filter(category => 
          category.is_active && 
          category.name.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 3)
      
      searchResults.value = {
        products: productsResponse.data || [],
        categories: filteredCategories,
        sets: productsResponse.sets || []
      }
      
      isDropdownOpen.value = true
      selectedIndex.value = -1
    } catch (error) {
      console.error('Search error:', error)
      searchResults.value = { products: [], categories: [] }
    } finally {
      isLoading.value = false
    }
  }
  
  const debouncedSearch = (query) => {
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value)
    }
    
    searchTimeout.value = setTimeout(() => {
      performSearch(query)
    }, 300)
  }
  
  const handleInput = (value) => {
    searchQuery.value = value
    if (value.length >= 2) {
      debouncedSearch(value)
    } else {
      closeDropdown()
    }
  }
  
  const handleKeydown = (event) => {
    if (!isDropdownOpen.value || !hasResults.value) return
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        selectedIndex.value = Math.min(selectedIndex.value + 1, totalResults.value - 1)
        break
        
      case 'ArrowUp':
        event.preventDefault()
        selectedIndex.value = Math.max(selectedIndex.value - 1, -1)
        break
        
      case 'Enter':
        event.preventDefault()
        if (selectedIndex.value >= 0) {
          selectItem(allItems.value[selectedIndex.value])
        } else if (searchQuery.value.trim()) {
          navigateToSearch()
        }
        break
        
      case 'Escape':
        event.preventDefault()
        closeDropdown()
        break
    }
  }
  
  const selectItem = (item) => {
    if (item.type === 'category') {
      router.push(`/category/${item.data.slug}`)
    } else if (item.type === 'product') {
      router.push(`/products/${item.data.slug}`)
    } else if (item.type === 'set') {
      router.push(`/sets/${item.data.slug}`)
    }
    closeDropdown()
  }
  
  const navigateToSearch = () => {
    if (searchQuery.value.trim()) {
      router.push({
        path: '/products',
        query: { search: searchQuery.value.trim() }
      })
      closeDropdown()
    }
  }
  
  const closeDropdown = () => {
    isDropdownOpen.value = false
    selectedIndex.value = -1
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value)
    }
  }
  
  const clearSearch = () => {
    searchQuery.value = ''
    searchResults.value = { products: [], categories: [], sets: [] }
    closeDropdown()
  }

  // Safely split text into segments, marking parts that match the query
  const splitHighlight = (text, query) => {
    if (!text) return []
    if (!query) return [{ text, highlight: false }]

    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const pattern = new RegExp(`(${escapeRegex(query)})`, 'gi')
    const parts = text.split(pattern)
    const qLower = String(query).toLowerCase()
    // parts alternates between non-matches and matches due to capturing group
    return parts
      .filter(part => part !== '')
      .map(part => ({ text: part, highlight: part.toLowerCase() === qLower }))
  }

  // Backward-compatible: return sanitized HTML string using the same split logic
  const highlightMatch = (text, query) => {
    const escapeHtml = (s) => String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

    return splitHighlight(text || '', query)
      .map(seg => seg.highlight
        ? `<mark class="bg-yellow-200 text-gray-900">${escapeHtml(seg.text)}</mark>`
        : escapeHtml(seg.text))
      .join('')
  }
  
  // Watch for outside clicks
  const handleClickOutside = (event, dropdownRef) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
      closeDropdown()
    }
  }
  
  return {
    // State
    searchQuery,
    isDropdownOpen,
    isLoading,
    searchResults,
    selectedIndex,
    
    // Computed
    hasResults,
    totalResults,
    allItems,
    
    // Methods
    handleInput,
    handleKeydown,
    selectItem,
    navigateToSearch,
    closeDropdown,
    clearSearch,
    highlightMatch,
    splitHighlight,
    handleClickOutside
  }
}
